import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';
import { uploadFile } from '../../services/fileUploadService';
import { patchProfileFields } from '../../services/profileService';
import { startEmailVerification, startPhoneVerification, verifyEmail, verifyPhone } from '../../services/verificationService';
import { digitsOnly, formatSSN } from '../../utils/profileFieldMapping';
import '../../styles/ProfileComplete.css';

const CompleteProfile = () => {
  const { user, updateProfile, uploadProfilePicture, refreshAuthState, refreshProfile, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const { completionPercentage } = useProfileCompletion(user);
  const [activeSection, setActiveSection] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    ssn: '',
    otherGender: '',
  });
  
  const [bioData, setBioData] = useState({
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
  } as { dateOfBirth?: string; gender?: string; maritalStatus?: string });
  
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  } as { street?: string; city?: string; state?: string; zipCode?: string; country?: string });
  
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [emailOtpToken, setEmailOtpToken] = useState('');
  const [phoneOtpToken, setPhoneOtpToken] = useState('');
  const [securityPhoto, setSecurityPhoto] = useState<File | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);

  // ✅ NEW: Refresh profile on mount to ensure complete data
  useEffect(() => {
    const initializeProfile = async () => {
      try {
        // If user has no ID, try refreshing profile
        if (user && !user.id) {
          console.warn('⚠️ User profile missing ID, attempting refresh...');
          const refreshed = await refreshProfile();
          if (!refreshed?.id) {
            console.error('❌ Profile still missing ID after refresh');
            setMessage({ 
              type: 'error', 
              text: 'Your profile could not be loaded properly. Please log in again.' 
            });
          }
        }
      } catch (error) {
        console.error('Error refreshing profile on mount:', error);
      }
    };
    
    initializeProfile();
  }, []);

  useEffect(() => {
    if (user && user.id) {
      setFormData({
        firstName: user.firstName || '',
        middleName: user.middleName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        ssn: '',
        otherGender: (user as any).otherGender || '',
      });
      
      setBioData({
        dateOfBirth: user.birthDate || user.bioData?.dateOfBirth || '',
        gender: user.gender || user.bioData?.gender || '',
        maritalStatus: user.bioData?.maritalStatus || '',
      });
      
      if (user.permanentAddress || user.address) {
        setAddress((prev) => ({ ...prev, ...(user.permanentAddress || user.address) }));
      }
    } else if (user && !user.id) {
      // ✅ NEW: Warn user if profile is incomplete
      console.warn('⚠️ User profile incomplete:', { 
        hasEmail: !!user.email, 
        hasFirstName: !!user.firstName,
        hasId: !!user.id 
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBioData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddress(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (type: 'profile' | 'security', file: File) => {
    try {
      if (type === 'profile') {
        setProfilePhoto(file);
        await uploadProfilePicture(file);
        await refreshProfile();
        setMessage({ type: 'success', text: 'Profile photo uploaded!' });
        return;
      }

      setSecurityPhoto(file);
      const token = getAccessToken();
      if (!token || !user?.id) throw new Error('You must be signed in to upload a security photo.');
      const result = await uploadFile(user.id, file, { purpose: 'USER_PROFILE', fileType: 'PHOTO' });
      await patchProfileFields([{ name: 'securityPhoto', value: result.fileId }]);
      await refreshProfile();
      setMessage({ type: 'success', text: 'Security photo uploaded!' });
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to upload photo.';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleSendVerificationCode = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('You must be signed in.');
      const response = await startPhoneVerification(token, formData.phone);
      setPhoneOtpToken(response.otpToken || '');
      setShowVerification(true);
      setMessage({ type: 'success', text: 'Verification code sent to your phone!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token || !phoneOtpToken) throw new Error('Verification session is missing.');
      await verifyPhone(phoneOtpToken, verificationCode, token);
      await refreshProfile();
      setMessage({ type: 'success', text: 'Phone verified successfully!' });
      setShowVerification(false);
      refreshAuthState();
    } catch (error) {
      setMessage({ type: 'error', text: 'Invalid verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailVerification = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('You must be signed in.');
      const response = await startEmailVerification(token);
      setEmailOtpToken(response.otpToken || '');
      setShowEmailVerification(true);
      setMessage({ type: 'success', text: 'Verification code sent to your email!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send email verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      if (!token || !emailOtpToken) throw new Error('Verification session is missing.');
      await verifyEmail(emailOtpToken, verificationCode, token);
      await refreshProfile();
      setMessage({ type: 'success', text: 'Email verified successfully!' });
      refreshAuthState();
    } catch (error) {
      setMessage({ type: 'error', text: 'Invalid email verification code.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      // ✅ NEW: Validate user ID exists before saving
      if (!user?.id) {
        console.error('❌ Cannot save profile: user ID is missing');
        setMessage({ 
          type: 'error', 
          text: 'Your profile could not be loaded. Please log in again to complete your profile.' 
        });
        setIsLoading(false);
        return;
      }

      const cleanSsn = digitsOnly(formData.ssn);
      if (cleanSsn && cleanSsn.length !== 9) {
        setMessage({ type: 'error', text: 'Please enter a valid 9-digit SSN or leave it blank.' });
        setIsLoading(false);
        return;
      }

      await updateProfile({
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        username: formData.username,
        phone: formData.phone,
        ssn: cleanSsn || undefined,
        birthDate: bioData.dateOfBirth,
        gender: bioData.gender,
        otherGender: bioData.gender === 'OTHER' ? formData.otherGender : undefined,
        bioData: {
          dateOfBirth: bioData.dateOfBirth,
          gender: bioData.gender,
          maritalStatus: bioData.maritalStatus,
        },
        permanentAddress: address,
        address,
      });
      await refreshProfile();
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      refreshAuthState();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile.' });
    } finally {
      setIsLoading(false);
    }
  };

  const sections = [
    { id: 'basic', name: 'Basic Info', icon: '👤', completed: !!formData.firstName && !!formData.lastName },
    { id: 'contact', name: 'Contact', icon: '📞', completed: !!formData.phone },
    { id: 'bio', name: 'Bio Data', icon: '📋', completed: !!bioData.dateOfBirth && !!bioData.gender && (bioData.gender !== 'OTHER' || !!formData.otherGender) },
    { id: 'address', name: 'Address', icon: '📍', completed: !!address.street && !!address.city && !!address.country },
    { id: 'photos', name: 'Photos', icon: '📸', completed: !!(user?.picture || user?.profilePhoto || profilePhoto) },
    { id: 'verification', name: 'Verification', icon: '✅', completed: user?.phoneVerified === true },
  ];

  return (
    <div className="complete-profile-container">
      <div className="complete-profile-header">
        <h1>Complete Your Profile</h1>
        <p>Please provide the following information to continue</p>
        <div className="profile-progress">
          <div className="progress-text">{Math.round(completionPercentage)}% Complete</div>
          <div className="progress-bar-large">
            <div className="progress-fill-large" style={{ width: `${completionPercentage}%` }}></div>
          </div>
        </div>
      </div>

      <div className="complete-profile-content">
        {/* Sidebar Navigation */}
        <div className="profile-sidebar">
          {sections.map(section => (
            <button
              key={section.id}
              className={`sidebar-item ${activeSection === section.id ? 'active' : ''} ${section.completed ? 'completed' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="sidebar-icon">{section.icon}</span>
              <span className="sidebar-name">{section.name}</span>
              {section.completed && <span className="sidebar-check">✓</span>}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="profile-main">
          {message && (
            <div className={`profile-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Basic Info Section */}
          {activeSection === 'basic' && (
            <div className="profile-section">
              <h2>Account & Basic Info</h2>
              
              <div className="form-row-2">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  placeholder="Enter your middle name"
                />
              </div>

              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Choose a username"
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  disabled
                />
                {!user?.emailVerified && (
                  <>
                    <button className="verify-btn" onClick={handleSendEmailVerification} disabled={isLoading}>
                      {showEmailVerification ? 'Resend Email Code' : 'Verify Email'}
                    </button>
                    {showEmailVerification && (
                      <div className="verification-input" style={{ marginTop: '0.75rem' }}>
                        <input
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                        />
                        <button className="btn-verify" onClick={handleVerifyEmailCode} disabled={isLoading}>
                          Verify Email Code
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="form-group">
                <label>SSN (For Identity Verification)</label>
                <input
                  type="text"
                  name="ssn"
                  value={formatSSN(formData.ssn)}
                  onChange={(event) => setFormData((prev) => ({ ...prev, ssn: event.target.value }))}
                  placeholder="XXX-XX-XXXX"
                  inputMode="numeric"
                  maxLength={11}
                />
                <small>Used for identity verification. Stored securely.</small>
              </div>
            </div>
          )}

          {/* Contact Section */}
          {activeSection === 'contact' && (
            <div className="profile-section">
              <h2>Phone Number</h2>
              
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 234 567 8900"
                />
              </div>

              {formData.phone && !user?.phoneVerified && (
                <div className="verification-box">
                  <p>We'll send a verification code to {formData.phone}</p>
                  {!showVerification ? (
                    <button className="btn-send-code" onClick={handleSendVerificationCode} disabled={isLoading}>
                      Send Verification Code
                    </button>
                  ) : (
                    <div className="verification-input">
                      <input
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                      />
                      <button className="btn-verify" onClick={handleVerifyCode} disabled={isLoading}>
                        Verify
                      </button>
                    </div>
                  )}
                </div>
              )}

              {user?.phoneVerified && (
                <div className="verified-badge">
                  ✓ Phone number verified
                </div>
              )}
            </div>
          )}

          {/* Bio Data Section */}
          {activeSection === 'bio' && (
            <div className="profile-section">
              <h2>Bio Data</h2>
              
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={bioData.dateOfBirth}
                  onChange={handleBioChange}
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Gender</label>
                  <select name="gender" value={bioData.gender} onChange={handleBioChange}>
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Marital Status</label>
                  <select name="maritalStatus" value={bioData.maritalStatus} onChange={handleBioChange}>
                    <option value="">Select Status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
              </div>

              {bioData.gender === 'OTHER' && (
                <div className="form-group">
                  <label>Specify Gender *</label>
                  <input
                    type="text"
                    name="otherGender"
                    value={formData.otherGender}
                    onChange={handleInputChange}
                    placeholder="Specify gender"
                  />
                </div>
              )}

            </div>
          )}

          {/* Address Section */}
          {activeSection === 'address' && (
            <div className="profile-section">
              <h2>Address</h2>
              
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={address.street}
                  onChange={handleAddressChange}
                  placeholder="Street address"
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={address.city}
                    onChange={handleAddressChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={address.state}
                    onChange={handleAddressChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={address.zipCode}
                    onChange={handleAddressChange}
                    placeholder="ZIP Code"
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select name="country" value={address.country} onChange={handleAddressChange}>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Photos Section */}
          {activeSection === 'photos' && (
            <div className="profile-section">
              <h2>Profile & Security Photos</h2>
              
              <div className="photos-grid">
                <div className="photo-card">
                  <div className="photo-preview">
                    {user?.picture || user?.profilePhoto ? (
                      <img src={user.picture || user.profilePhoto} alt="Profile" />
                    ) : profilePhoto ? (
                      <img src={URL.createObjectURL(profilePhoto)} alt="Profile preview" />
                    ) : (
                      <div className="photo-placeholder">📷</div>
                    )}
                  </div>
                  <h4>Profile Photo</h4>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handlePhotoUpload('profile', e.target.files[0])}
                    id="profile-photo"
                    style={{ display: 'none' }}
                  />
                  <button className="btn-upload" onClick={() => document.getElementById('profile-photo')?.click()}>
                    Upload Photo
                  </button>
                </div>

                <div className="photo-card">
                  <div className="photo-preview">
                    {securityPhoto ? (
                      <img src={URL.createObjectURL(securityPhoto)} alt="Security" />
                    ) : user?.securityPhotoAt ? (
                      <img src={user.securityPhotoAt} alt="Security" />
                    ) : (
                      <div className="photo-placeholder">🔒</div>
                    )}
                  </div>
                  <h4>Security Photo</h4>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handlePhotoUpload('security', e.target.files[0])}
                    id="security-photo"
                    style={{ display: 'none' }}
                  />
                  <button className="btn-upload" onClick={() => document.getElementById('security-photo')?.click()}>
                    Upload Photo
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Verification Section */}
          {activeSection === 'verification' && (
            <div className="profile-section">
              <h2>Identity Verification</h2>
              
              <div className="verification-status">
                <div className="status-item">
                  <span>Email Verification:</span>
                  {user?.emailVerified ? (
                    <span className="verified">✓ Verified</span>
                  ) : (
                    <button className="verify-link" onClick={() => setActiveSection('basic')}>Verify Email</button>
                  )}
                </div>
                <div className="status-item">
                  <span>Phone Verification:</span>
                  {user?.phoneVerified ? (
                    <span className="verified">✓ Verified</span>
                  ) : (
                    <button className="verify-link" onClick={() => setActiveSection('contact')}>Verify Phone</button>
                  )}
                </div>
                <div className="status-item">
                  <span>Identity Verification:</span>
                  {user?.identityVerified ? (
                    <span className="verified">✓ Verified</span>
                  ) : (
                    <button className="verify-link" onClick={() => navigate('/account/profile-details')}>Start Verification</button>
                  )}
                </div>
              </div>

              <div className="identity-info">
                <p>To verify your identity, you'll need to provide:</p>
                <ul>
                  <li>Government-issued ID (Driver's License, Passport)</li>
                  <li>Selfie with your ID</li>
                  <li>Proof of address (utility bill, bank statement)</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="profile-actions">
            <button className="btn-save-all" onClick={handleSaveAll} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
